import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  Lock,
  ArrowRight,
  Gauge,
  MapPin,
  ShieldCheck,
  Eye,
  EyeOff,
} from 'lucide-react';
import MainService from '../services/MainService';
import { clearPageAccessCache, isPrivilegedRole, loadAllowedPageNames } from '../services/pageAccess';
import {
  cachePortal,
  clearManagementCache,
  fetchCustomerPortal,
  getRegisterCodeFromProfile,
  isSaRole,
  setAccountSuspended,
} from '../services/managementService';

const SESSION_EXPIRED_STORAGE_KEY = 'ainsoft.sessionExpiredMessage';

// Full-page background (map-style artwork) — same as earlier login screen.
const LOGIN_PAGE_MAP_BG = '/images/banner.png';
// Hero image on the left panel of the login card (public/images/drobo.png).
const LOGIN_CARD_HERO_IMAGE = '/images/drobo.png';

function Login() {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    financialYearId: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [companyProfile, setCompanyProfile] = useState({});
  const [financialYears, setFinancialYears] = useState([]);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [boxMounted, setBoxMounted] = useState(false);
  const [cardHeroMissing, setCardHeroMissing] = useState(false);
  const formRef = useRef(null);
  const navigate = useNavigate();

  const portalName =
    companyProfile.CompanyName ||
    companyProfile.Name ||
    'Ainsoft Delivery';

  useEffect(() => {
    const t = setTimeout(() => setBoxMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!profileLoaded) {
      loadCompanyProfile();
    }
  }, [profileLoaded]);

  useEffect(() => {
    const savedUsername = localStorage.getItem('loginRememberUsername');
    if (savedUsername) {
      setFormData((prev) => ({ ...prev, username: savedUsername }));
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    try {
      const msg = sessionStorage.getItem(SESSION_EXPIRED_STORAGE_KEY);
      if (msg) {
        setError(msg);
        sessionStorage.removeItem(SESSION_EXPIRED_STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const loadCompanyProfile = async () => {
    try {
      setLoading(true);
      const response = await MainService.GetCompanyProfile();
      if (response != null) {
        setCompanyProfile(response);
        if (response.FinancialYearId > 0) {
          setFormData((prev) => ({ ...prev, financialYearId: response.FinancialYearId }));
        }
        setProfileLoaded(true);
      } else {
        setCompanyProfile({});
        setProfileLoaded(true);
      }
    } catch (err) {
      console.error('Error loading company profile:', err);
      setCompanyProfile({});
      setProfileLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (!formData.username) {
      setError('Username is required');
      setLoading(false);
      return;
    }
    if (!formData.password) {
      setError('Password is required');
      setLoading(false);
      return;
    }
    try {
      const loginData = {
        UserName: formData.username,
        Password: formData.password,
        FinancialYearId: formData.financialYearId || companyProfile.FinancialYearId,
      };
      const response = await MainService.Login(loginData);
      if (response) {
        const userData = response;
        const validRoles = ['Admin', 'SA', 'CA', 'Employee'];
        if (validRoles.includes(userData.Role)) {
          const tokenInfo = {
            ...userData,
            FinancialYearId: formData.financialYearId || companyProfile.FinancialYearId,
          };
          localStorage.setItem('authToken', tokenInfo.Token);
          localStorage.setItem('userInfo', JSON.stringify(tokenInfo));
          localStorage.setItem('companyProfile', JSON.stringify(companyProfile));
          clearPageAccessCache();
          clearManagementCache();

          // Management license / payment gate (SA bypasses overdue suspension)
          let navigateTo = '/home';
          const registerCode = getRegisterCodeFromProfile(companyProfile);
          if (registerCode) {
            try {
              const portal = await fetchCustomerPortal(registerCode);
              cachePortal(portal);
              if (portal.Found && portal.IsAccessBlocked && !isSaRole(userData.Role)) {
                setAccountSuspended(true);
                navigateTo = '/home/account-suspended';
              } else {
                setAccountSuspended(false);
              }
            } catch (mgmtErr) {
              console.warn('Management portal check failed:', mgmtErr);
              setAccountSuspended(false);
            }
          }

          if (!isPrivilegedRole(userData.Role)) {
            try {
              await loadAllowedPageNames(userData.Id);
            } catch (accessErr) {
              console.warn('Failed to preload page access:', accessErr);
            }
          }
          if (rememberMe) {
            localStorage.setItem('loginRememberUsername', formData.username);
          } else {
            localStorage.removeItem('loginRememberUsername');
          }
          setLoginSuccess(true);
          setError('');
          setTimeout(() => navigate(navigateTo), 1500);
        } else {
          setError('Invalid username or password');
        }
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-[10px]">
      <div className="absolute inset-0 z-0 overflow-hidden" aria-hidden>
        <div
          className="login-bg-image-layer absolute inset-0 bg-cover bg-center bg-no-repeat will-change-transform"
          style={{ backgroundImage: `url('${LOGIN_PAGE_MAP_BG}')` }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(135deg, rgba(255, 255, 255, 0.22) 0%, rgba(248, 252, 250, 0.18) 50%, rgba(255, 255, 255, 0.22) 100%)',
          }}
        />
        <div className="login-bg-gradient-shift absolute inset-0 pointer-events-none bg-gradient-to-br from-emerald-200/25 via-transparent to-primary/20" />
        <div className="login-bg-orb login-bg-orb-a pointer-events-none absolute -top-[18%] -left-[12%] h-[min(70vw,520px)] w-[min(70vw,520px)] rounded-full bg-primary/30 blur-[72px]" />
        <div className="login-bg-orb login-bg-orb-b pointer-events-none absolute -bottom-[22%] -right-[14%] h-[min(75vw,560px)] w-[min(75vw,560px)] rounded-full bg-emerald-400/35 blur-[88px]" />
        <div className="login-bg-orb login-bg-orb-c pointer-events-none absolute left-[25%] top-[35%] h-[min(45vw,380px)] w-[min(45vw,380px)] rounded-full bg-lime-300/25 blur-[64px]" />
        <div className="login-bg-particles absolute inset-0 pointer-events-none">
          {[...Array(40)].map((_, i) => (
            <span
              key={i}
              className="login-bg-particle login-bg-particle-dot absolute rounded-full bg-primary/55 shadow-[0_0_14px_rgba(70,149,90,0.45)]"
              style={{
                width: 6 + (i % 5),
                height: 6 + (i % 5),
                left: `${(i * 7 + 3) % 96}%`,
                top: `${(i * 11 + 5) % 92}%`,
                animationDelay: `${(i * 0.14) % 5}s`,
                animationDuration: `${4.2 + (i % 6) * 0.35}s`,
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        .login-bg-image-layer {
          transform: scale(1.08);
          animation: login-bg-kenburns 28s ease-in-out infinite alternate;
        }
        @keyframes login-bg-kenburns {
          0% { transform: scale(1.04) translate(0%, 0%); }
          100% { transform: scale(1.18) translate(-2.2%, -1.6%); }
        }
        .login-bg-orb-a {
          animation: login-bg-float-a 18s ease-in-out infinite;
        }
        .login-bg-orb-b {
          animation: login-bg-float-b 22s ease-in-out infinite;
        }
        .login-bg-orb-c {
          animation: login-bg-float-c 15s ease-in-out infinite;
        }
        @keyframes login-bg-float-a {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.9; }
          50% { transform: translate(5vw, 5vh) scale(1.12); opacity: 1; }
        }
        @keyframes login-bg-float-b {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.85; }
          50% { transform: translate(-5vw, -4vh) scale(1.1); opacity: 1; }
        }
        @keyframes login-bg-float-c {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.8; }
          50% { transform: translate(3vw, -5vh) scale(1.14); opacity: 1; }
        }
        .login-bg-gradient-shift {
          animation: login-bg-shimmer 10s ease-in-out infinite alternate;
        }
        @keyframes login-bg-shimmer {
          0% { opacity: 0.45; }
          100% { opacity: 0.95; }
        }
        .login-bg-particle {
          animation: login-bg-particle-float ease-in-out infinite;
        }
        @keyframes login-bg-particle-float {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          35% { transform: translate(18px, -22px) scale(1.2); opacity: 1; }
          70% { transform: translate(-14px, 14px) scale(0.92); opacity: 0.65; }
        }
        .login-hero-fly {
          animation: login-hero-fly 5.2s ease-in-out infinite;
          transform-origin: 55% 45%;
          will-change: transform;
        }
        @keyframes login-hero-fly {
          0%, 100% { transform: translate(0, 0) rotate(-2.5deg) scale(1); }
          20% { transform: translate(6%, -12%) rotate(2deg) scale(1.03); }
          45% { transform: translate(-4%, -20%) rotate(-1.5deg) scale(1); }
          70% { transform: translate(8%, -10%) rotate(3deg) scale(1.04); }
          85% { transform: translate(2%, -4%) rotate(0.5deg) scale(1.01); }
        }
        @media (prefers-reduced-motion: reduce) {
          .login-bg-image-layer,
          .login-bg-orb,
          .login-bg-gradient-shift,
          .login-bg-particle,
          .login-hero-fly {
            animation: none !important;
          }
          .login-bg-image-layer { transform: scale(1.06); }
          .login-hero-fly { transform: none; }
        }
        .login-card-enter {
          opacity: 0;
          transform: translateY(16px) scale(0.99);
        }
        .login-card-enter-active {
          opacity: 1;
          transform: translateY(0) scale(1);
          transition: opacity 0.5s ease-out, transform 0.5s ease-out;
        }
        .signin-btn-arrow {
          animation: signin-arrow-nudge 2s ease-in-out infinite;
        }
        @keyframes signin-arrow-nudge {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(4px); }
        }
      `}</style>

      <div
        className={`relative z-10 w-[80vw] max-w-[calc(100vw-20px)] h-[80vh] max-h-[calc(100dvh-20px)] transition-all duration-500 ${
          boxMounted ? 'login-card-enter-active' : 'login-card-enter'
        }`}
      >
        <div className="flex flex-col lg:flex-row h-full min-h-0 overflow-y-auto rounded-3xl bg-white shadow-[0_25px_80px_-12px_rgba(20,83,45,0.22)] border border-green-100/80">
          {/* Promotional panel — below form on small screens */}
          <aside
            className="relative order-2 lg:order-1 lg:w-[50%] flex flex-col justify-between bg-gradient-to-br from-[#ecfdf5] via-[#d8f3dc] to-[#c7e9d0] px-8 py-8 sm:px-10 sm:py-10 lg:px-14 lg:py-12 overflow-hidden min-h-0 lg:overflow-y-auto"
            style={{
              backgroundImage: 'url(/images/banner2.png)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'bottom',
            }}
          >
            <div
              className="pointer-events-none absolute -right-20 top-1/4 h-[420px] w-[420px] rounded-full bg-primary/15 blur-3xl"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -left-16 bottom-0 h-72 w-72 rounded-full bg-[#166534]/10 blur-2xl"
              aria-hidden
            />

            <div className="relative z-10 min-h-0">
              <div className="mb-5">
                <img
                  src="/images/logo2.png"
                  alt={portalName}
                  className="h-20 sm:h-24 w-auto max-w-[200px] sm:max-w-none object-contain object-left"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/images/logo.png';
                  }}
                />
              </div>

              <h1 className="text-2xl sm:text-3xl lg:text-[1.85rem] font-extrabold leading-tight text-gray-900 mb-3">
                Complete <span className="text-primary">Delivery Solution</span> for UAE Businesses
              </h1>
              <p className="text-gray-600 text-sm sm:text-base max-w-lg leading-relaxed mb-4">
                Streamline your delivery operations with our comprehensive suite of applications. From rider
                management to customer tracking, we&apos;ve got you covered.
              </p>

              <div className="relative mx-auto max-w-sm lg:max-w-none mb-4 min-h-[8.5rem] sm:min-h-[10rem] lg:min-h-[11rem] flex items-center justify-center overflow-visible">
                {!cardHeroMissing && (
                  <div className="login-hero-fly w-full h-36 sm:h-40 lg:h-44 flex items-center justify-center">
                    <img
                      src={LOGIN_CARD_HERO_IMAGE}
                      alt=""
                      className="h-full w-full max-h-full object-contain object-center drop-shadow-[0_12px_28px_rgba(20,83,45,0.18)]"
                      onError={() => setCardHeroMissing(true)}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="relative z-10 grid grid-cols-3 gap-2 sm:gap-3 pt-2 border-t border-green-800/20 shrink-0">
              {[
                { icon: Gauge, label: 'Fast & Reliable' },
                { icon: MapPin, label: 'Live Tracking' },
                { icon: ShieldCheck, label: 'Secure & Safe' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex flex-col items-center text-center gap-2">
                  <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-white text-primary shadow-md ring-1 ring-black/5">
                    <Icon className="w-4 h-4 sm:w-[18px] sm:h-[18px]" strokeWidth={2.25} />
                  </div>
                  <span className="rounded-full bg-white/85 px-2 py-0.5 text-[10px] sm:text-[11px] font-bold text-[#0f3d22] leading-tight shadow-sm backdrop-blur-sm">
                    {label}
                  </span>
                </div>
              ))}
            </div>

            <p className="relative z-10 mt-4 shrink-0 text-center">
              <span className="inline-block rounded-full bg-white/85 px-3 py-1 text-xs font-semibold text-[#14532d] shadow-sm backdrop-blur-sm">
                Powered by{' '}
                <a
                  href="https://ainsoftsolutions.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-bold hover:underline"
                >
                  Ainsoft Solutions
                </a>
              </span>
            </p>
          </aside>

          {/* Login form — window dots only (top-right) */}
          <div className="order-1 lg:order-2 lg:w-[50%] flex flex-col bg-white min-h-0 relative lg:overflow-y-auto">
            <div
              className="absolute top-5 right-5 sm:top-6 sm:right-6 lg:top-8 lg:right-10 flex items-center gap-1.5 z-10 pointer-events-none"
              aria-hidden="true"
            >
              <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
              <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
              <span className="w-3 h-3 rounded-full bg-[#28c840]" />
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 pt-12 pb-8 sm:px-11 sm:pt-14 sm:pb-10 lg:px-16 lg:py-12 min-h-0">
              <div className="w-full max-w-2xl mx-auto">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#14532d] mb-2">Welcome Back!</h2>
                <p className="text-gray-500 text-sm mb-10 sm:mb-12">
                  Login to continue to {portalName}.
                </p>

                <div className="w-full max-w-xs sm:max-w-sm mx-auto">
                  <form ref={formRef} className="space-y-4" onSubmit={handleSubmit}>
                    <div>
                      <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Email / Username
                      </label>
                      <div className="relative">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                          <Mail className="w-5 h-5" />
                        </div>
                        <input
                          id="username"
                          name="username"
                          type="text"
                          required
                          autoComplete="username"
                          className="w-full pl-11 pr-4 py-3 text-base bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                          placeholder="Enter your email or username"
                          value={formData.username}
                          onChange={handleChange}
                          disabled={loading || loginSuccess}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Password
                      </label>
                      <div className="relative">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                          <Lock className="w-5 h-5" />
                        </div>
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          required
                          autoComplete="current-password"
                          className="w-full pl-11 pr-12 py-3 text-base bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                          placeholder="Enter your password"
                          value={formData.password}
                          onChange={handleChange}
                          disabled={loading || loginSuccess}
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    {companyProfile.IsAccounting && financialYears.length > 0 && (
                      <div>
                        <label htmlFor="financialYearId" className="block text-sm font-medium text-gray-700 mb-1.5">
                          Financial year
                        </label>
                        <select
                          id="financialYearId"
                          name="financialYearId"
                          value={formData.financialYearId}
                          onChange={handleChange}
                          required
                          disabled={loading || loginSuccess}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                        >
                          <option value="">Select Financial Year</option>
                          {financialYears.map((year) => (
                            <option key={year.Id} value={year.Id}>
                              {year.Name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {error && (
                      <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-xl text-sm">
                        {error}
                      </div>
                    )}

                    {loginSuccess && (
                      <div className="bg-green-50 border border-green-100 text-green-700 px-4 py-3 rounded-xl text-sm">
                        Login successful! Redirecting...
                      </div>
                    )}

                    <div className="flex items-center justify-between gap-3 pt-1">
                      <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-gray-600">Remember me</span>
                      </label>
                      <a
                        href="https://ainsoftdelivery.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-primary hover:text-green-700"
                      >
                        Forgot password?
                      </a>
                    </div>

                    <button
                      type="submit"
                      disabled={loading || loginSuccess}
                      className="group w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-primary hover:bg-green-600 text-white font-semibold text-base shadow-lg shadow-primary/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span>{loading ? 'Signing in...' : loginSuccess ? 'Success!' : 'Login'}</span>
                      <ArrowRight className="w-5 h-5 signin-btn-arrow" />
                    </button>
                  </form>

                  <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center" aria-hidden>
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs sm:text-sm">
                      <span className="bg-white px-4 text-gray-400">or continue with</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      className="flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      Google
                    </button>
                    <button
                      type="button"
                      className="flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                        <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                      </svg>
                      Apple
                    </button>
                  </div>
                </div>

                <p className="mt-8 text-center text-sm text-gray-600">
                  Don&apos;t have an account?{' '}
                  <a
                    href="https://ainsoftdelivery.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-primary hover:text-green-700"
                  >
                    Sign up
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
